import { getDateNow } from 'odfjell-date-time-utils/dateTimeUtils';
import { useLocation } from 'react-router-dom';
import useAppFeature from './useAppFeature';
import { AppFeatureEnum } from 'src/enums/appFeatureEnum';
import { useVesselStore } from 'src/context/VesselContext';
import { useStowagePlanStore } from 'src/context/StowagePlanContext';
import { useEventStore } from 'src/context/EventContext';
import { resolveNotFoundPlanStatus } from 'src/utils/stowage/eventsPlanStatusUtils';

export default () => {
    const { isFeatureEnabled } = useAppFeature();
    const { selectedVessel } = useVesselStore();
    const { masterPlanLoadState, masterPlanLoadStateImo } = useStowagePlanStore();
    const { latestVoyageData } = useEventStore();
    const location = useLocation();

    // Returns true while the master stowage plan fetch has not yet resolved, indicating callers
    // should wait before loading rotation / event data. 'idle' means the fetch has not been
    // kicked off yet (it is set to 'loading' by a separate effect higher in the component tree —
    // see useSetData's Effect 3 — which is not guaranteed to have run before this check on the
    // very first render), so it must block exactly like 'loading' does; treating it as safe to
    // proceed would let AOL/rotation data load before the stowage plan, producing false event
    // mismatches. Unblocks once the fetch reaches a terminal state (loaded, not-found,
    // error) — including when no plan exists for the vessel. Simple-mode vessels never need
    // stowage plan context, so they are always unblocked. useSetData's Effect 3 intentionally
    // never starts the fetch while the user is on the '/stowage' page (masterPlanLoadState stays
    // 'idle' there indefinitely by design), so that route must be excluded here too, or callers
    // mounted independently of the active route (e.g. SignalR update handlers) would block
    // forever while the user stays on '/stowage'.
    //
    // Readiness state must be scoped to the vessel it describes: masterPlanLoadState is stored
    // globally while the plan itself is reset per vessel on vessel change, so a stale terminal
    // state (e.g. 'loaded') left over from the previous vessel could otherwise be misread as
    // ready before the newly selected vessel's own fetch has (re)started. masterPlanLoadStateImo
    // is set together with every masterPlanLoadState transition, so treating an IMO mismatch the
    // same as 'idle'/'loading' closes that race regardless of which effect runs first.
    //
    // A terminal 'error' or an unverified 'not-found' must also block callers: 'not-found' only
    // unblocks once resolveNotFoundPlanStatus returns 'ready' (verified legacy); 'no-plan' and the
    // pre-voyage-context 'loading' both block, exactly like idle/loading.
    //
    // resolveNotFoundPlanStatus must never be handed a previous vessel's voyage context.
    // latestVoyageData is only trusted here when it is tagged (imoNumber) for the same vessel
    // currently being checked; a mismatch is treated identically to "not yet resolved"
    // (undefined), which falls through to resolveNotFoundPlanStatus's 'loading' branch.
    const isUnverifiedNotFound = (imoNumber: number): boolean => {
        if (masterPlanLoadState !== 'not-found') {
            return false;
        }
        const currentVoyageNumberForVessel = latestVoyageData?.imoNumber === imoNumber ? latestVoyageData?.voyageNumber1 : undefined;
        return resolveNotFoundPlanStatus(selectedVessel?.firstVoyageOrcaWebStowageImosSync, currentVoyageNumberForVessel) !== 'ready';
    };

    const checkLoadedStowageIntegration = (imoNumber: number): boolean =>
        isFeatureEnabled({ featureEnum: AppFeatureEnum.StowageIntegration, imoNumber, date: getDateNow() }) &&
        location.pathname !== '/stowage' &&
        (masterPlanLoadState === 'idle' ||
            masterPlanLoadState === 'loading' ||
            (masterPlanLoadStateImo != null && masterPlanLoadStateImo !== imoNumber) ||
            masterPlanLoadState === 'error' ||
            isUnverifiedNotFound(imoNumber)) &&
        !selectedVessel?.hasOrcaWebSimpleMode;

    return {
        checkLoadedStowageIntegration,
    };
};
